import { useState, FormEvent, useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { PawPrint } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, mensajeError, codigoError } from "../api/client";
import { getTenantSubdomain } from "../lib/subdomain";
import { Spinner } from "../components/ui/Spinner";

interface MarcaNegocio {
  nombre: string;
  logoUrl: string | null;
  colorPrimario: string | null;
}

function PerritoTriste() {
  return (
    <svg viewBox="0 0 200 160" className="h-32 w-32">
      <ellipse cx="100" cy="140" rx="70" ry="10" fill="#e2e8f0" />
      <ellipse cx="60" cy="55" rx="22" ry="30" fill="#94a3b8" transform="rotate(-25 60 55)" />
      <ellipse cx="140" cy="55" rx="22" ry="30" fill="#94a3b8" transform="rotate(25 140 55)" />
      <circle cx="100" cy="80" r="55" fill="#cbd5e1" />
      <circle cx="80" cy="80" r="8" fill="#334155" />
      <circle cx="120" cy="80" r="8" fill="#334155" />
      <path d="M74 96 Q78 104 84 98" stroke="#334155" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M126 96 Q122 104 116 98" stroke="#334155" strokeWidth="2" fill="none" strokeLinecap="round" />
      <ellipse cx="100" cy="98" rx="10" ry="7" fill="#334155" />
      <path d="M90 112 Q100 104 110 112" stroke="#334155" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M78 90 Q75 100 79 108" stroke="#7dd3fc" strokeWidth="3" fill="none" strokeLinecap="round" />
      <circle cx="79" cy="110" r="3" fill="#7dd3fc" />
    </svg>
  );
}

export default function Login() {
  const { login, usuario } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [negocioInactivo, setNegocioInactivo] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [marca, setMarca] = useState<MarcaNegocio | null>(null);

  useEffect(() => {
    if (searchParams.get("inactivo") === "1") setNegocioInactivo(true);
  }, [searchParams]);

  // Se pide ANTES de autenticarse (endpoint público, ver auth.routes.ts) para
  // poder mostrar el logo y nombre reales del negocio desde la pantalla de
  // login, no solo el subdominio en crudo. Si falla (ej. subdominio raro o
  // sin internet), simplemente se cae al ícono/nombre genérico de respaldo,
  // sin bloquear el login.
  useEffect(() => {
    api
      .get<MarcaNegocio>("/auth/negocio-actual")
      .then((res) => setMarca(res.data))
      .catch(() => setMarca(null));
  }, []);

  if (usuario) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setCargando(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      if (codigoError(err) === "NEGOCIO_INACTIVO") {
        setNegocioInactivo(true);
      } else {
        setError(mensajeError(err));
      }
    } finally {
      setCargando(false);
    }
  }

  if (negocioInactivo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-white px-4">
        <div className="w-full max-w-sm card p-8 text-center">
          <div className="mb-4 flex justify-center"><PerritoTriste /></div>
          <h1 className="text-lg font-semibold text-slate-800">Este negocio está inactivo</h1>
          <p className="mt-2 text-sm text-slate-500">
            Contacta a Mascotas Vet para reactivar tu cuenta y volver a usar la plataforma.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50 to-white px-4">
      <div className="w-full max-w-sm card p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          {marca?.logoUrl ? (
            <img
              src={marca.logoUrl}
              alt={`Logotipo de ${marca.nombre}`}
              className="mb-3 h-20 w-20 rounded-3xl border border-slate-100 object-cover shadow-sm"
            />
          ) : (
            <div
              className="mb-3 flex h-20 w-20 items-center justify-center rounded-3xl text-white"
              style={{ backgroundColor: marca?.colorPrimario || "#0EA5A4" }}
            >
              <PawPrint size={36} />
            </div>
          )}
          <h1 className="text-lg font-semibold text-slate-800">Bienvenido de nuevo</h1>
          <p className="mt-1 text-base font-medium text-slate-600">{marca?.nombre || getTenantSubdomain()}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">Correo electrónico</label>
            <input type="email" className="input" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label">Contraseña</label>
            <input type="password" className="input" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" className="btn-primary w-full" disabled={cargando}>
            {cargando && <Spinner className="h-4 w-4 text-white" />}
            Iniciar sesión
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-400">
          ¿Tu negocio aún no está registrado?{" "}
          <Link to="/registro" className="font-medium text-brand-600 hover:underline">Crear cuenta</Link>
        </p>
      </div>
    </div>
  );
}
